/**
 * TreatmentPlanFromNoteBanner
 *
 * Slide-in banner shown above a note when the title looks like a
 * treatment plan ("treatment plan" / "mtp" / "tp" — word-boundary,
 * case-insensitive). Clicking "Save as plan" confirms and invokes the
 * v2 `/treatment-plan/from-note` endpoint, then opens the edit modal
 * so the provider can review and tweak.
 *
 * Dismissal is per-note, persisted in sessionStorage so it survives
 * remounts in the same tab but doesn't follow the user forever.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { Close as CloseIcon, Spa as SpaIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import {
  useGetTreatmentPlanQuery,
  useSaveNoteAsTreatmentPlanMutation,
} from '../store/api';

import { TREATMENT_PLAN_EDIT_DIALOG_DEFAULTS } from './PatientTreatmentPlanEditModal.constants';

const TREATMENT_PLAN_TITLE_RE =
  /\b(treatment\s*plan|mtp|tp)\b/i;

const dismissKey = (noteId: string) => `tp-from-note-dismissed:${noteId}`;

export interface TreatmentPlanFromNoteBannerProps {
  noteId: string | null | undefined;
  patientId: string | null | undefined;
  noteTitle: string | null | undefined;
  /**
   * Gate: only render the banner once the note has finished generating
   * (sections populated, not streaming). Parent owns the composite
   * check so the banner stays decoupled from note-ai-edit internals.
   */
  isReady?: boolean;
}

export const TreatmentPlanFromNoteBanner: React.FC<
  TreatmentPlanFromNoteBannerProps
> = ({ noteId, patientId, noteTitle, isReady = true }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { open: openModal } = useModal();

  const [dismissed, setDismissed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveNoteAsTreatmentPlan, { isLoading: isSaving }] =
    useSaveNoteAsTreatmentPlanMutation();

  const titleMatches = useMemo(
    () => !!noteTitle && TREATMENT_PLAN_TITLE_RE.test(noteTitle),
    [noteTitle],
  );

  // Peek at the existing plan only when the title matches — otherwise the
  // banner will never render and the extra fetch is wasted on every note
  // view. RTK Query dedupes against the context-bar's own fetch once
  // titleMatches flips true.
  const { data: existingPlan } = useGetTreatmentPlanQuery(
    { patientId: patientId ?? '' },
    { skip: !patientId || !titleMatches || !isReady },
  );
  const hasExistingPlan = !!existingPlan?.treatment_plan_id;

  // Re-read dismissed state when noteId changes
  useEffect(() => {
    if (!noteId) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(sessionStorage.getItem(dismissKey(noteId)) === '1');
    } catch {
      setDismissed(false);
    }
  }, [noteId]);

  const shouldShow =
    !!noteId && !!patientId && titleMatches && !dismissed && isReady;

  const handleDismiss = () => {
    setDismissed(true);
    if (noteId) {
      try {
        sessionStorage.setItem(dismissKey(noteId), '1');
      } catch {
        /* ignore quota / privacy mode */
      }
    }
  };

  const handleConfirm = async () => {
    if (!noteId || !patientId) return;
    try {
      await saveNoteAsTreatmentPlan({
        patientId,
        saveNoteAsTreatmentPlanRequest: { note_id: noteId },
      }).unwrap();
      setConfirmOpen(false);
      // Dismiss the banner so it doesn't re-appear behind the edit modal
      // and linger after the provider closes it. The plan is saved;
      // showing the prompt again on this same note is noise.
      handleDismiss();
      enqueueSnackbar('Note saved as treatment plan.', { variant: 'success' });
      openModal(MODAL_TYPES.TREATMENT_PLAN_EDIT, {
        patientId,
        source: 'from_note_banner',
        ...TREATMENT_PLAN_EDIT_DIALOG_DEFAULTS,
      });
    } catch (error) {
      console.error('Failed to save note as treatment plan', error);
      enqueueSnackbar('Could not save as treatment plan', { variant: 'error' });
    }
  };

  if (!shouldShow) return null;

  return (
    <>
      <Slide direction="down" in={shouldShow} mountOnEnter unmountOnExit>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: '8px',
              bgcolor: 'background.paper',
              borderLeft: `12px solid ${theme.palette.success.main}`,
              boxShadow: '0px 3px 6px rgba(0,0,0,0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0px 4px 10px rgba(0,0,0,0.18)',
              },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                Save this note as the treatment plan?
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', lineHeight: 1.4 }}
              >
                {hasExistingPlan
                  ? 'Replaces the current plan. The previous version is kept in history.'
                  : 'Starts a new treatment plan with this note as the content.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={<SpaIcon />}
              onClick={() => setConfirmOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Save as plan
            </Button>
            <IconButton
              size="small"
              onClick={handleDismiss}
              aria-label="Dismiss treatment plan suggestion"
              sx={{ color: 'text.secondary', flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Slide>

      <Dialog
        open={confirmOpen}
        onClose={() => (isSaving ? undefined : setConfirmOpen(false))}
        fullWidth
       maxWidth={"xs"}>
        <DialogTitle>
          {hasExistingPlan
            ? 'Replace current treatment plan?'
            : 'Save as treatment plan?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {hasExistingPlan
              ? 'The current treatment plan will be archived to history (restorable from the editor).'
              : 'This note\u2019s content will be copied into a new treatment plan.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="success"
            disabled={isSaving}
          >
            {isSaving
              ? 'Saving\u2026'
              : hasExistingPlan
              ? 'Replace'
              : 'Save as plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
